/**
 * Module dependencies.
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import moment from 'moment';
import { Title } from 'app/components/atoms/title/title';
import { Spinner } from 'app/components/ui/spinner';
import { DataTable } from 'app/components/ui/data-table';
import { cn } from 'app/lib/utils';
import { HandCoins } from 'lucide-react';
import { CgLoanEvent, useCryptoGestLoans } from 'app/hooks/requests/crypto-gest/use-crypto-gest';
import { StatCard } from '../stat-card';
import { amount, formatInCurrency } from '../format';
import { useDisplayMoney } from '../use-display-money';
import { LoanCalculator, LoanHistoryRow } from './loan-calculator';
import { DualMoney, useToEur } from './dual-money';

/**
 * Credit lines are keyed by Nexo's "Credit Line" column; older exports have none.
 */

const LEGACY = '__legacy__';

/**
 * Number from a decimal string.
 */

function num(value: string | null | undefined): number {
  return parseFloat(value ?? '0') || 0;
}

/**
 * Loan amounts are in a USD-pegged unit (USD / USDX / xUSD) — show them as USD.
 */

function loanMoney(value: number): string {
  return formatInCurrency(value, 'USD');
}

/**
 * What was received / paid: euros (EUR / EURX) as €, anything else as "n SYMBOL".
 */

function isEuro(currency: string | null): boolean {
  return currency === 'EUR' || currency === 'EURX';
}

function counterMoney(value: string | null, currency: string | null): string {
  if (value == null || !currency) {
    return '—';
  }

  return isEuro(currency) ? formatInCurrency(num(value), 'EUR') : `${amount(value)} ${currency}`;
}

/**
 * Sum counter amounts per currency (EURX folded into EUR) → "€2,450.00 + 100 USDT".
 */

function counterTotals(events: CgLoanEvent[]): string {
  const totals = new Map<string, number>();

  for (const event of events) {
    if (event.counterAmount == null || !event.counterCurrency) {
      continue;
    }

    const currency = isEuro(event.counterCurrency) ? 'EUR' : event.counterCurrency;

    totals.set(currency, (totals.get(currency) ?? 0) + num(event.counterAmount));
  }

  return Array.from(totals.entries())
    .sort(([a], [b]) => (a === 'EUR' ? -1 : b === 'EUR' ? 1 : a.localeCompare(b)))
    .map(([currency, value]) => (currency === 'EUR' ? formatInCurrency(value, 'EUR') : `${amount(value)} ${currency}`))
    .join(' + ');
}

/**
 * EUR for part of a repayment at the rate it was actually paid at (€ paid ÷ USD
 * repaid), so its principal + interest add up to the euros paid; null (→ today's
 * rate) when it wasn't paid in euros.
 */

function atRepaymentRate(
  row: { total: number; counterAmount: string | null; counterCurrency: string | null },
  usd: number
): number | null {
  return isEuro(row.counterCurrency) && row.total > 0 ? (usd * num(row.counterAmount)) / row.total : null;
}

/**
 * A repayment row: the repayment plus the interest settled at the same instant.
 */

type RepaymentRow = {
  id: string;
  timestamp: string;
  total: number;
  interest: number;
  counterAmount: string | null;
  counterCurrency: string | null;
};

/**
 * Per-credit-line figures.
 */

function summarize(events: CgLoanEvent[]) {
  const withdrawals = events.filter(event => event.kind === 'withdrawal');
  const repayments = events.filter(event => event.kind === 'repayment');
  const interest = events.filter(event => event.kind === 'interest');
  const daily = events.filter(event => event.kind === 'daily_interest');

  const sum = (list: CgLoanEvent[]) => list.reduce((total, event) => total + num(event.amount), 0);
  const borrowed = sum(withdrawals);
  const repaid = sum(repayments);
  const interestSettled = sum(interest);
  const interestDaily = sum(daily);
  // Repayments settle the accrued interest first; the rest reduces principal.
  const outstanding = borrowed - (repaid - interestSettled);

  // Fold the interest settled with each repayment into its row (same instant).
  const interestByInstant = new Map<string, number>();

  for (const event of interest) {
    interestByInstant.set(event.timestamp, (interestByInstant.get(event.timestamp) ?? 0) + num(event.amount));
  }

  const repaymentRows: RepaymentRow[] = repayments.map(event => {
    const settled = interestByInstant.get(event.timestamp) ?? 0;

    interestByInstant.delete(event.timestamp);

    return {
      id: event.id,
      timestamp: event.timestamp,
      total: num(event.amount),
      interest: settled,
      counterAmount: event.counterAmount,
      counterCurrency: event.counterCurrency
    };
  });

  // Interest settled without a matching repayment row (rare) still shows up.
  interestByInstant.forEach((value, timestamp) => {
    repaymentRows.push({
      id: `interest-${timestamp}`,
      timestamp,
      total: value,
      interest: value,
      counterAmount: null,
      counterCurrency: null
    });
  });

  // Interest by month: the daily accrued postings (with the first/last day they
  // cover) and the interest settled with repayments in that month. Nexo posts
  // the daily ones at 00:00 UTC — group and show in UTC so a day never shifts.
  type MonthRow = {
    month: string;
    days: number;
    total: number;
    settled: number;
    from: string | null;
    to: string | null;
    // Annualised rate implied by each daily posting (% per year), and the
    // balance-weighted sums for the month's average.
    rates: number[];
    weightedInterest: number;
    weightedBalance: number;
    // Latest daily posting, and — for the month still running — the projected
    // total at month end (that posting repeated for the remaining days).
    lastAmount: number;
    forecast: number | null;
  };

  const months = new Map<string, MonthRow>();
  const monthOf = (timestamp: string): MonthRow => {
    const month = moment.utc(timestamp).format('YYYY-MM');
    const entry = months.get(month) ?? {
      month,
      days: 0,
      total: 0,
      settled: 0,
      from: null,
      to: null,
      rates: [],
      weightedInterest: 0,
      weightedBalance: 0,
      lastAmount: 0,
      forecast: null
    };

    months.set(month, entry);

    return entry;
  };

  // Walk the line chronologically keeping the estimated balance, so each daily
  // posting can be read as a rate: interest × 365 ÷ balance owed that day.
  let balance = 0;
  // Rate implied by the most recent daily posting — the rate in force now.
  let currentRate: number | null = null;
  const chronological = [...events].sort((a, b) =>
    a.timestamp < b.timestamp ? -1 : a.timestamp > b.timestamp ? 1 : 0
  );

  for (const event of chronological) {
    const value = num(event.amount);

    if (event.kind === 'withdrawal') {
      balance += value;
    } else if (event.kind === 'repayment') {
      balance -= value;
    } else if (event.kind === 'interest') {
      balance += value;
    } else {
      const entry = monthOf(event.timestamp);

      entry.days += 1;
      entry.total += value;
      entry.from = entry.from == null || event.timestamp < entry.from ? event.timestamp : entry.from;
      if (entry.to == null || event.timestamp >= entry.to) {
        entry.to = event.timestamp;
        entry.lastAmount = value;
      }

      if (balance > 1) {
        currentRate = ((value * 365) / balance) * 100;
        entry.rates.push(currentRate);
        entry.weightedInterest += value * 365;
        entry.weightedBalance += balance;
      }
    }
  }

  for (const event of interest) {
    monthOf(event.timestamp).settled += num(event.amount);
  }

  // The current (UTC) month isn't over: project its total to month end.
  const current = months.get(moment.utc().format('YYYY-MM'));

  if (current?.to) {
    const last = moment.utc(current.to);
    const remaining = last.daysInMonth() - last.date();

    current.forecast = current.total + current.settled + remaining * current.lastAmount;
  }

  // What already happened on the line, oldest first, with the balance after
  // each step — the calculator shows it ahead of the projected payments.
  const history: LoanHistoryRow[] = [];
  let running = 0;
  const steps = [
    ...withdrawals.map(event => ({ timestamp: event.timestamp, withdrawal: event, repayment: null })),
    ...repaymentRows.map(row => ({ timestamp: row.timestamp, withdrawal: null, repayment: row }))
  ].sort((a, b) => (a.timestamp < b.timestamp ? -1 : a.timestamp > b.timestamp ? 1 : 0));

  for (const step of steps) {
    if (step.withdrawal) {
      const usdValue = num(step.withdrawal.amount);

      running += usdValue;
      history.push({
        key: step.withdrawal.id,
        date: step.withdrawal.timestamp,
        kind: 'withdrawal',
        eur: isEuro(step.withdrawal.counterCurrency) ? num(step.withdrawal.counterAmount) : null,
        usd: usdValue,
        interest: 0,
        principal: usdValue,
        remaining: running
      });
    } else if (step.repayment) {
      const principal = step.repayment.total - step.repayment.interest;

      running -= principal;
      history.push({
        key: step.repayment.id,
        date: step.repayment.timestamp,
        kind: 'repayment',
        eur: isEuro(step.repayment.counterCurrency) ? num(step.repayment.counterAmount) : null,
        usd: step.repayment.total,
        interest: step.repayment.interest,
        principal,
        remaining: Math.max(running, 0)
      });
    }
  }

  return {
    withdrawals,
    repayments,
    history,
    repaymentRows: repaymentRows.sort((a, b) => (a.timestamp < b.timestamp ? 1 : -1)),
    dailyByMonth: Array.from(months.values()).sort((a, b) => (a.month < b.month ? 1 : -1)),
    borrowed,
    repaid,
    interestSettled,
    interestDaily,
    outstanding,
    currentRate,
    lastAt: events.reduce((latest, event) => (event.timestamp > latest ? event.timestamp : latest), '')
  };
}

/**
 * Annualised rate for a month (or the totals): a single figure when stable, a
 * "low – high" range when it changed (e.g. 8.1% – 16.3%). Rates are read off
 * 2-decimal daily postings, so they're approximate.
 */

type RateSource = { rates: number[]; weightedInterest: number; weightedBalance: number };

function rateLabel(source: RateSource): string {
  if (!source.rates.length || source.weightedBalance <= 0) {
    return '—';
  }

  const low = Math.min(...source.rates);
  const high = Math.max(...source.rates);
  const pct = (value: number) => `${value.toFixed(1)}%`;

  // Postings are rounded to the cent, so a ~1 pp wobble is just rounding noise.
  return high - low < 1 ? pct((source.weightedInterest / source.weightedBalance) * 100) : `${pct(low)} – ${pct(high)}`;
}

/**
 * A month's interest total (€ / $), with the month-end forecast underneath in
 * brackets while the month is still running.
 */

function TotalWithForecast({ total, forecast }: { total: number; forecast: number | null }): JSX.Element {
  const [translate] = useTranslation();
  const toEur = useToEur();

  return (
    <div className={'text-right'}>
      <DualMoney usd={total} />
      {forecast != null && (
        <div
          className={'text-[11px] font-normal tabular-nums text-muted-foreground'}
          title={translate('cryptoGest.loans.forecastHint')}
        >
          {`(${formatInCurrency(toEur(forecast), 'EUR')} / ${formatInCurrency(forecast, 'USD')})`}
        </div>
      )}
    </div>
  );
}

/**
 * Section wrapper.
 */

function Section({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <section className={'space-y-2'}>
      <h2 className={'text-sm font-semibold uppercase tracking-wide text-muted-foreground'}>{title}</h2>
      <div className={'overflow-hidden rounded-xl border border-border bg-card shadow-sm'}>{children}</div>
    </section>
  );
}

/**
 * Export `CryptoGestLoans` component.
 *
 * Nexo credit-line activity: what was borrowed (and received in the bank), what
 * was repaid (and with what), the interest charged, and an outstanding estimate.
 */

export function CryptoGestLoans(): JSX.Element {
  const [translate] = useTranslation();
  const { data, isLoading } = useCryptoGestLoans();
  const { usdPerEur } = useDisplayMoney();
  const toEur = useToEur();
  const [selected, setSelected] = useState<string | null>(null);

  // Credit lines, most recently active first.
  const lines = useMemo(() => {
    const byLine = new Map<string, CgLoanEvent[]>();

    for (const event of data?.events ?? []) {
      const key = event.creditLine || LEGACY;
      const list = byLine.get(key) ?? [];

      list.push(event);
      byLine.set(key, list);
    }

    return Array.from(byLine.entries())
      .map(([key, events]) => ({ key, ...summarize(events) }))
      .sort((a, b) => (a.lastAt < b.lastAt ? 1 : -1));
  }, [data]);

  const line = lines.find(item => item.key === selected) ?? lines[0];
  const lineLabel = (key: string) => (key === LEGACY ? translate('cryptoGest.loans.legacy') : key);

  const withdrawalColumns = useMemo<ColumnDef<CgLoanEvent, any>[]>(
    () => [
      {
        id: 'date',
        accessorFn: row => row.timestamp,
        header: translate('cryptoGest.loans.date'),
        cell: ({ row }) => moment(row.original.timestamp).format('DD MMM YYYY, HH:mm')
      },
      {
        id: 'received',
        header: translate('cryptoGest.loans.receivedCol'),
        cell: ({ row }) =>
          isEuro(row.original.counterCurrency) ? (
            <DualMoney
              align={'left'}
              className={'font-medium text-foreground'}
              eur={num(row.original.counterAmount)}
              usd={num(row.original.amount)}
            />
          ) : (
            <span className={'font-medium tabular-nums text-foreground'}>
              {counterMoney(row.original.counterAmount, row.original.counterCurrency)}
            </span>
          )
      },
      {
        id: 'debt',
        header: () => <div className={'text-right'}>{translate('cryptoGest.loans.debt')}</div>,
        cell: ({ row }) => (
          <DualMoney
            eur={isEuro(row.original.counterCurrency) ? num(row.original.counterAmount) : null}
            usd={num(row.original.amount)}
          />
        )
      }
    ],
    [translate]
  );

  const repaymentColumns = useMemo<ColumnDef<RepaymentRow, any>[]>(
    () => [
      {
        id: 'date',
        accessorFn: row => row.timestamp,
        header: translate('cryptoGest.loans.date'),
        cell: ({ row }) => moment(row.original.timestamp).format('DD MMM YYYY, HH:mm')
      },
      {
        id: 'paid',
        header: translate('cryptoGest.loans.paidCol'),
        cell: ({ row }) =>
          isEuro(row.original.counterCurrency) || row.original.counterCurrency == null ? (
            <DualMoney
              align={'left'}
              className={'font-medium text-foreground'}
              eur={atRepaymentRate(row.original, row.original.total)}
              usd={row.original.total}
            />
          ) : (
            <span className={'font-medium tabular-nums text-foreground'}>
              {counterMoney(row.original.counterAmount, row.original.counterCurrency)}
            </span>
          )
      },
      {
        id: 'principal',
        header: () => <div className={'text-right'}>{translate('cryptoGest.loans.principal')}</div>,
        cell: ({ row }) => {
          const principal = row.original.total - row.original.interest;

          return <DualMoney eur={atRepaymentRate(row.original, principal)} usd={principal} />;
        }
      },
      {
        id: 'interest',
        header: () => <div className={'text-right'}>{translate('cryptoGest.loans.interestCol')}</div>,
        cell: ({ row }) =>
          row.original.interest > 0 ? (
            <DualMoney
              className={'text-muted-foreground'}
              eur={atRepaymentRate(row.original, row.original.interest)}
              usd={row.original.interest}
            />
          ) : (
            <div className={'text-right text-muted-foreground'}>{'—'}</div>
          )
      },
      {
        id: 'total',
        header: () => <div className={'text-right'}>{translate('cryptoGest.loans.total')}</div>,
        cell: ({ row }) => (
          <DualMoney eur={atRepaymentRate(row.original, row.original.total)} usd={row.original.total} />
        )
      }
    ],
    [translate]
  );

  if (isLoading) {
    return (
      <div className={'flex justify-center py-16'}>
        <Spinner />
      </div>
    );
  }

  if (!line) {
    return (
      <div className={'w-full space-y-5'}>
        <Title>{translate('cryptoGest.loans.title')}</Title>

        <div
          className={'flex flex-col items-center gap-2 rounded-xl border border-dashed border-border p-10 text-center'}
        >
          <HandCoins className={'h-8 w-8 text-muted-foreground'} />
          <div className={'font-medium'}>{translate('cryptoGest.loans.empty.title')}</div>
          <p className={'max-w-md text-sm text-muted-foreground'}>{translate('cryptoGest.loans.empty.description')}</p>
        </div>
      </div>
    );
  }

  const dailyTotals = line.dailyByMonth.reduce(
    (totals, month) => ({
      days: totals.days + month.days,
      total: totals.total + month.total,
      settled: totals.settled + month.settled,
      rates: totals.rates.concat(month.rates),
      weightedInterest: totals.weightedInterest + month.weightedInterest,
      weightedBalance: totals.weightedBalance + month.weightedBalance,
      // Grand total with the running month at its projection instead.
      forecast:
        month.forecast != null || totals.forecast != null
          ? (totals.forecast ?? totals.total + totals.settled) + (month.forecast ?? month.total + month.settled)
          : null
    }),
    {
      days: 0,
      total: 0,
      settled: 0,
      rates: [] as number[],
      weightedInterest: 0,
      weightedBalance: 0,
      forecast: null as number | null
    }
  );
  const settled = line.outstanding <= 0.01;
  // Euros for a set of loan movements: the exact € received/paid when known,
  // otherwise today's conversion.
  const eurOf = (list: CgLoanEvent[]) =>
    list.reduce(
      (sum, event) => sum + (isEuro(event.counterCurrency) ? num(event.counterAmount) : toEur(num(event.amount))),
      0
    );
  const pair = (usd: number) => `${formatInCurrency(toEur(usd), 'EUR')} / ${formatInCurrency(usd, 'USD')}`;
  const outstandingEur = usdPerEur > 0 ? Math.max(line.outstanding, 0) / usdPerEur : null;

  return (
    <div className={'w-full space-y-5'}>
      <div className={'flex flex-wrap items-end justify-between gap-3'}>
        <div>
          <Title>{translate('cryptoGest.loans.title')}</Title>
          <p className={'text-sm text-muted-foreground'}>{translate('cryptoGest.loans.subtitle')}</p>
        </div>

        {lines.length > 1 && (
          <div className={'inline-flex rounded-lg border border-border bg-card p-0.5 text-sm'}>
            {lines.map(item => (
              <button
                className={cn(
                  'rounded-md px-3 py-1.5 font-medium transition-colors',
                  item.key === line.key
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
                key={item.key}
                onClick={() => setSelected(item.key)}
                type={'button'}
              >
                {lineLabel(item.key)}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className={'grid gap-3 sm:grid-cols-2 lg:grid-cols-4'}>
        <div title={translate('cryptoGest.loans.outstandingHint')}>
          <StatCard
            label={translate('cryptoGest.loans.outstanding')}
            sub={
              settled ? translate('cryptoGest.loans.settled') : formatInCurrency(Math.max(line.outstanding, 0), 'USD')
            }
            subClass={settled ? 'text-emerald-600' : undefined}
            value={formatInCurrency(outstandingEur ?? 0, 'EUR')}
          />
        </div>

        <StatCard
          label={translate('cryptoGest.loans.borrowed')}
          sub={`${loanMoney(line.borrowed)} · ${translate('cryptoGest.loans.received', {
            value: counterTotals(line.withdrawals) || '—'
          })}`}
          value={formatInCurrency(eurOf(line.withdrawals), 'EUR')}
        />

        <StatCard
          label={translate('cryptoGest.loans.repaid')}
          sub={`${loanMoney(line.repaid)} · ${translate('cryptoGest.loans.paid', {
            value: counterTotals(line.repayments) || '—'
          })}`}
          value={formatInCurrency(eurOf(line.repayments), 'EUR')}
        />

        <StatCard
          label={translate('cryptoGest.loans.interest')}
          sub={`${loanMoney(line.interestSettled + line.interestDaily)} · ${translate('cryptoGest.loans.interestSub', {
            settled: pair(line.interestSettled),
            daily: pair(line.interestDaily)
          })}`}
          value={formatInCurrency(toEur(line.interestSettled + line.interestDaily), 'EUR')}
        />
      </div>

      <Section title={translate('cryptoGest.loans.withdrawals')}>
        <DataTable
          columns={withdrawalColumns}
          data={line.withdrawals}
          emptyMessage={translate('cryptoGest.loans.noResults')}
          initialSorting={[{ id: 'date', desc: true }]}
          pageSize={10}
        />
      </Section>

      <Section title={translate('cryptoGest.loans.repayments')}>
        <DataTable
          columns={repaymentColumns}
          data={line.repaymentRows}
          emptyMessage={translate('cryptoGest.loans.noResults')}
          initialSorting={[{ id: 'date', desc: true }]}
          pageSize={10}
        />
      </Section>

      {line.dailyByMonth.length > 0 && (
        <Section title={translate('cryptoGest.loans.dailyInterest')}>
          <table className={'w-full text-sm'}>
            <thead>
              <tr className={'border-b border-border text-left text-xs text-muted-foreground'}>
                <th className={'px-4 py-2 font-medium'}>{translate('cryptoGest.loans.month')}</th>
                <th className={'px-4 py-2 font-medium'}>{translate('cryptoGest.loans.from')}</th>
                <th className={'px-4 py-2 font-medium'}>{translate('cryptoGest.loans.to')}</th>
                <th className={'px-4 py-2 text-right font-medium'}>{translate('cryptoGest.loans.days')}</th>
                <th className={'px-4 py-2 text-right font-medium'} title={translate('cryptoGest.loans.rateHint')}>
                  {translate('cryptoGest.loans.rate')}
                </th>
                <th className={'px-4 py-2 text-right font-medium'}>{translate('cryptoGest.loans.dailyAccrued')}</th>
                <th className={'px-4 py-2 text-right font-medium'}>{translate('cryptoGest.loans.settledCol')}</th>
                <th className={'px-4 py-2 text-right font-medium'}>{translate('cryptoGest.loans.totalCol')}</th>
              </tr>
            </thead>
            <tbody>
              {line.dailyByMonth.map(month => (
                <tr className={'border-b border-border last:border-0'} key={month.month}>
                  <td className={'px-4 py-2'}>{moment(`${month.month}-01`).format('MMMM YYYY')}</td>
                  <td className={'px-4 py-2 tabular-nums'}>
                    {month.from ? moment.utc(month.from).format('DD MMM YYYY') : '—'}
                  </td>
                  <td className={'px-4 py-2 tabular-nums'}>
                    {month.to ? moment.utc(month.to).format('DD MMM YYYY') : '—'}
                  </td>
                  <td className={'px-4 py-2 text-right tabular-nums'}>{month.days || '—'}</td>
                  <td className={'px-4 py-2 text-right tabular-nums'}>{rateLabel(month)}</td>
                  <td className={'px-4 py-2 text-right'}>{month.days ? <DualMoney usd={month.total} /> : '—'}</td>
                  <td className={'px-4 py-2 text-right'}>
                    {month.settled > 0 ? <DualMoney usd={month.settled} /> : '—'}
                  </td>
                  <td className={'px-4 py-2 font-medium'}>
                    <TotalWithForecast forecast={month.forecast} total={month.total + month.settled} />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className={'border-t border-border bg-muted/40 font-semibold'}>
                <td className={'px-4 py-2'} colSpan={3}>
                  {translate('cryptoGest.loans.total')}
                </td>
                <td className={'px-4 py-2 text-right tabular-nums'}>{dailyTotals.days || '—'}</td>
                <td className={'px-4 py-2 text-right tabular-nums'}>{rateLabel(dailyTotals)}</td>
                <td className={'px-4 py-2'}>
                  <DualMoney usd={dailyTotals.total} />
                </td>
                <td className={'px-4 py-2'}>
                  <DualMoney usd={dailyTotals.settled} />
                </td>
                <td className={'px-4 py-2'}>
                  <TotalWithForecast forecast={dailyTotals.forecast} total={dailyTotals.total + dailyTotals.settled} />
                </td>
              </tr>
            </tfoot>
          </table>
        </Section>
      )}

      {!settled && (
        <Section title={translate('cryptoGest.loans.calculator.title')}>
          <LoanCalculator
            apr={line.currentRate}
            balanceUsd={Math.max(line.outstanding, 0)}
            history={line.history}
            usdPerEur={usdPerEur}
          />
        </Section>
      )}
    </div>
  );
}
